/**
 * Custom typed event emitter for the Flow Engine
 * Pure TypeScript implementation with no external dependencies
 * Adapted for React environment with proper error handling
 */

import { logger } from '../../utils/logger';

import type { FlowEventHandler, FlowEventMap, FlowEventType } from './types';


// Create component logger for event emitter
const eventEmitterLogger = logger.withComponent('EventEmitter');

/**
 * Type-safe event emitter for flow engine events
 * 
 * @example
 * ```typescript
 * const emitter = new TypedEventEmitter();
 * 
 * // Subscribe to events
 * emitter.on('stepChanged', (event) => {
 *   eventEmitterLogger.info('Step changed', { from: event.from?.id, to: event.to.id });
 * });
 * 
 * // Emit events
 * emitter.emit('stepChanged', { from: null, to: step });
 * ```
 */
export class TypedEventEmitter {
  private listeners: Map<string, Set<Function>> = new Map();

  /**
   * Subscribe to an event
   * 
   * @param event - Event type to listen for
   * @param handler - Function to call when event is emitted
   */
  on<T extends FlowEventType>(event: T, handler: FlowEventHandler<T>): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(handler);
  }

  /**
   * Unsubscribe from an event
   * 
   * @param event - Event type to stop listening for
   * @param handler - Specific handler to remove
   */
  off<T extends FlowEventType>(event: T, handler: FlowEventHandler<T>): void {
    const handlers = this.listeners.get(event);
    if (handlers) {
      handlers.delete(handler);
      if (handlers.size === 0) {
        this.listeners.delete(event);
      }
    }
  }

  /**
   * Subscribe to an event for one-time execution
   * Handler is automatically removed after first emission
   * 
   * @param event - Event type to listen for once
   * @param handler - Function to call when event is emitted
   */
  once<T extends FlowEventType>(event: T, handler: FlowEventHandler<T>): void {
    const onceHandler = (data: FlowEventMap[T]) => {
      this.off(event, onceHandler);
      handler(data);
    };
    this.on(event, onceHandler);
  }

  /**
   * Emit an event to all subscribed handlers
   * Handlers are called synchronously in registration order
   * 
   * @param event - Event type to emit
   * @param data - Event data payload
   */
  emit<T extends FlowEventType>(event: T, data: FlowEventMap[T]): void {
    const handlers = this.listeners.get(event);
    if (handlers) {
      // Create a copy to avoid issues if handlers are modified during iteration
      const handlerArray = Array.from(handlers);
      handlerArray.forEach((handler) => {
        try {
          handler(data);
        } catch (error) {
          eventEmitterLogger.error(`Error in event handler for ${event}`, error);
          // Don't let handler errors break other handlers
        }
      });
    }
  }

  /**
   * Remove all event listeners for a specific event type
   * 
   * @param event - Event type to clear, or undefined to clear all
   */
  removeAllListeners(event?: FlowEventType): void {
    if (event) {
      this.listeners.delete(event);
    } else {
      this.listeners.clear();
    }
  }

  /**
   * Get the number of listeners for an event type
   * 
   * @param event - Event type to check
   * @returns Number of registered listeners
   */
  listenerCount(event: FlowEventType): number {
    const handlers = this.listeners.get(event);
    return handlers ? handlers.size : 0;
  }

  /**
   * Get all event types that have listeners
   * 
   * @returns Array of event type names
   */
  eventNames(): FlowEventType[] {
    return Array.from(this.listeners.keys()) as FlowEventType[];
  }

  /**
   * Check if there are any listeners for an event type
   * 
   * @param event - Event type to check
   * @returns True if there are listeners
   */
  hasListeners(event: FlowEventType): boolean {
    return this.listenerCount(event) > 0;
  }
}